import {useCallback, useState} from 'react';

export interface DateRange {
	from: string;
	to: string;
}

export interface DashboardFilterState {
	assignee: string[];
	requester: string[];
	status: string[];
	priority: string[];
	channel: string[];
	tags: string[];
	slaState: string[];
	lastActivityRange: DateRange;
	resolvedDateRange: DateRange;
	firstResponseRange: DateRange;
}

const EMPTY_DATE_RANGE: DateRange = {from: '', to: ''};

export const EMPTY_DASHBOARD_FILTERS: DashboardFilterState = {
	assignee: [],
	requester: [],
	status: [],
	priority: [],
	channel: [],
	tags: [],
	slaState: [],
	lastActivityRange: EMPTY_DATE_RANGE,
	resolvedDateRange: EMPTY_DATE_RANGE,
	firstResponseRange: EMPTY_DATE_RANGE,
};

function cloneFilters(f: DashboardFilterState): DashboardFilterState {
	return {
		...f,
		assignee: [...f.assignee],
		requester: [...f.requester],
		status: [...f.status],
		priority: [...f.priority],
		channel: [...f.channel],
		tags: [...f.tags],
		slaState: [...f.slaState],
		lastActivityRange: {...f.lastActivityRange},
		resolvedDateRange: {...f.resolvedDateRange},
		firstResponseRange: {...f.firstResponseRange},
	};
}

interface PanelState {
	isOpen: boolean;
	/** The last set of filters committed by Apply. */
	applied: DashboardFilterState;
	/** In-progress edits inside the open panel. */
	draft: DashboardFilterState;
}

/**
 * Open/draft/applied live in ONE state object, and every transition reads the
 * previous value through the functional updater rather than through a closure.
 *
 * The three are interdependent — `open` seeds draft from applied, `applyDraft`
 * writes draft into applied — so separate `useState`s make each callback close
 * over a value that is only current as of the render that created it. Two
 * transitions dispatched from the same event (`updateDraft(...)` then
 * `applyDraft()`) would then have the second one act on the pre-update draft and
 * silently commit the wrong filters.
 */
export function useDashboardFilterPanel() {
	const [state, setState] = useState<PanelState>(() => ({
		isOpen: false,
		applied: cloneFilters(EMPTY_DASHBOARD_FILTERS),
		draft: cloneFilters(EMPTY_DASHBOARD_FILTERS),
	}));
	const {isOpen, applied, draft} = state;

	const open = useCallback(() => {
		setState(prev => ({...prev, isOpen: true, draft: cloneFilters(prev.applied)}));
	}, []);

	const close = useCallback(() => {
		setState(prev => ({...prev, isOpen: false}));
	}, []);

	const updateDraft = useCallback((partial: Partial<DashboardFilterState>) => {
		setState(prev => ({...prev, draft: {...prev.draft, ...partial}}));
	}, []);

	const clearDraft = useCallback(() => {
		setState(prev => ({...prev, draft: cloneFilters(EMPTY_DASHBOARD_FILTERS)}));
	}, []);

	const applyDraft = useCallback(() => {
		setState(prev => ({isOpen: false, draft: prev.draft, applied: cloneFilters(prev.draft)}));
	}, []);

	const hasActiveFilters =
		applied.assignee.length > 0 ||
		applied.requester.length > 0 ||
		applied.status.length > 0 ||
		applied.priority.length > 0 ||
		applied.channel.length > 0 ||
		applied.tags.length > 0 ||
		applied.slaState.length > 0 ||
		applied.lastActivityRange.from !== '' ||
		applied.lastActivityRange.to !== '' ||
		applied.resolvedDateRange.from !== '' ||
		applied.resolvedDateRange.to !== '' ||
		applied.firstResponseRange.from !== '' ||
		applied.firstResponseRange.to !== '';

	return {isOpen, draft, applied, open, close, updateDraft, clearDraft, applyDraft, hasActiveFilters};
}
